/**
 * DatePicker Component
 *
 * 날짜 선택 컴포넌트
 * - HTML5 <input type="date"> 사용 (모바일 네이티브 피커)
 * - min, max로 날짜 범위 제한 가능
 */

export interface DatePickerProps {
  label: string
  value: string // YYYY-MM-DD 형식
  onChange: (value: string) => void
  error?: string
  required?: boolean
  disabled?: boolean
  min?: string // YYYY-MM-DD 형식
  max?: string // YYYY-MM-DD 형식
}

export function DatePicker({
  label,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  min,
  max,
}: DatePickerProps) {
  const hasError = !!error

  // 베이스 스타일
  const baseClasses = 'w-full h-12 px-4 border rounded-lg text-base transition-colors'

  // 에러 상태
  const borderClasses = hasError
    ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:border-gray-600 dark:focus:ring-primary-400'

  // 비활성화 상태
  const disabledClasses = disabled
    ? 'bg-gray-100 cursor-not-allowed text-gray-500 dark:bg-gray-900 dark:text-gray-400'
    : 'bg-white dark:bg-gray-800 dark:text-gray-100'

  const inputClassName = [baseClasses, borderClasses, disabledClasses]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="w-full">
      {/* Label */}
      <label className="block mb-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-100">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
      </label>

      {/* Date Input */}
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        min={min}
        max={max}
        className={inputClassName}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${label}-error` : undefined}
        data-error={hasError}
      />

      {/* Error Message */}
      {hasError && (
        <p
          id={`${label}-error`}
          className="mt-1 text-sm text-red-600 dark:text-red-400"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  )
}
